import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  CircularProgress
} from '@mui/material';
import { FormField } from '../../types/form';
import api from '../../services/api';

// ============================================================================
// Interfaces
// ============================================================================
interface RelatedListEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: RelatedListConfig) => void;
  currentConfig?: RelatedListConfig;
  availableTables: string[];
  fields: FormField[];
}

export interface RelatedListConfig {
  id: string;
  tableName: string;
  label: string;
  fields: string[];
  relationshipField: string;
}

// ============================================================================
// Component
// ============================================================================
export const RelatedListEditor: React.FC<RelatedListEditorProps> = ({
  open,
  onClose,
  onSave,
  currentConfig,
  availableTables
}) => {
  const [config, setConfig] = useState<RelatedListConfig>(
    currentConfig || {
      id: Math.random().toString(36).substr(2, 9),
      tableName: '',
      label: '',
      fields: [],
      relationshipField: ''
    }
  );

  const [availableFields, setAvailableFields] = useState<FormField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  useEffect(() => {
    // Fetch fields for the selected related table
    const fetchFieldsForTable = async () => {
      if (!config.tableName) {
        setAvailableFields([]);
        return;
      }

      setLoadingFields(true);
      try {
        const response = await api.get(`/config/${config.tableName}`);
        setAvailableFields(response.data.fields || []);
      } catch (error) {
        console.error(`Error fetching fields for table ${config.tableName}:`, error);
        setAvailableFields([]);
      } finally {
        setLoadingFields(false);
      }
    };

    fetchFieldsForTable();
  }, [config.tableName]);

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Configure Related List</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            fullWidth
            label="Label"
            value={config.label}
            onChange={(e) => setConfig({ ...config, label: e.target.value })}
          />

          <FormControl fullWidth>
            <InputLabel>Related Table</InputLabel>
            <Select
              value={config.tableName}
              onChange={(e) => setConfig({ ...config, tableName: e.target.value })}
              label="Related Table"
            >
              {availableTables.map((table) => (
                <MenuItem key={table} value={table}>
                  {table}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingFields ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              Loading fields...
            </Box>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>Relationship Field</InputLabel>
                <Select
                  value={config.relationshipField}
                  onChange={(e) => setConfig({ ...config, relationshipField: e.target.value })}
                  label="Relationship Field"
                  disabled={!config.tableName}
                >
                  {availableFields.map((field) => (
                    <MenuItem key={field.field_name} value={field.field_name}>
                      {field.label || field.field_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <InputLabel>Display Fields</InputLabel>
                <List>
                  {availableFields.map((field) => (
                    <ListItem key={field.field_name}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={config.fields.includes(field.field_name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setConfig({
                                  ...config,
                                  fields: [...config.fields, field.field_name]
                                });
                              } else {
                                setConfig({
                                  ...config,
                                  fields: config.fields.filter((f) => f !== field.field_name)
                                });
                              }
                            }}
                          />
                        }
                        label={field.label || field.field_name}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loadingFields}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
